import type { Map as MapLibreMap } from "maplibre-gl";
import type { Feature } from "geojson";
import {
  getLayerStyleFromTheme,
  getLayerStyleForGeometry,
  getLayerZOrder,
  createThemedFillLayer,
  createThemedFillExtrusion,
  createThemedLineLayer,
  createPropSymbolLayer,
  createVignetteRingLayer,
  applyCustomPathOverrides,
  applyCustomPolygonOverrides,
  NATURE_PROPS,
  isPolygonLayerType,
  isLineLayerType,
  isDualShapeLayerType,
  type ThemeConfig,
} from "@wondermaps/maps";
import { PROP_TYPES } from "@wondermaps/db/client";
import type { Layer } from "../MapCanvas";

/**
 * Get the z-order for a layer, considering prop-specific z-orders
 */
function getLayerSortOrder(layer: Layer, theme: ThemeConfig): number {
  // For props, check theme.props[propType].zOrder
  if (layer.layerType === "prop") {
    const styleJson = layer.styleJson as { propType?: string } | null;
    const propType = styleJson?.propType || "tree";
    const propZOrder = theme.props?.[propType]?.zOrder;
    if (propZOrder !== undefined) return propZOrder;
  }

  // Baked shadow layers render just below their parent polygon
  const styleJson = layer.styleJson as Record<string, unknown> | null;
  if (styleJson?.isShadow) {
    return getLayerZOrder(layer.layerType, theme) - 0.01;
  }

  // Fall back to standard layer z-order
  return getLayerZOrder(layer.layerType, theme);
}

/**
 * Add a baked shadow layer to the map (published polygon shadow with real offset geometry)
 */
function addShadowLayer(
  map: MapLibreMap,
  layer: Layer,
  sourceId: string,
  feature: Feature,
): void {
  const styleJson = layer.styleJson as {
    shadowColor?: string;
    shadowOpacity?: number;
    shadowMinZoom?: number;
  } | null;

  const color = styleJson?.shadowColor ?? "#000000";
  const opacity = styleJson?.shadowOpacity ?? 0.3;
  const minZoom = styleJson?.shadowMinZoom ?? 15;

  map.addSource(sourceId, { type: "geojson", data: feature });
  map.addLayer({
    id: layer.id,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": color,
      "fill-opacity": [
        "interpolate", ["linear"], ["zoom"],
        minZoom - 1, 0,
        minZoom, opacity,
      ],
    },
  } as import("maplibre-gl").LayerSpecification);
}

/**
 * Add a vignette layer to the map
 * Vignette layers have inverted mask geometry (world with boundary as hole)
 */
function addVignetteLayer(
  map: MapLibreMap,
  layer: Layer,
  sourceId: string,
  theme: ThemeConfig
): void {
  const vignetteStyleJson = layer.styleJson as {
    ringIndex?: number;
    opacity?: number;
  } | null;

  // Read opacity from layer styleJson, fallback to theme config
  const layerOpacity = vignetteStyleJson?.opacity ?? theme.vignette?.opacity ?? 0.8;
  const maskColor = theme.colors.maskColor || "#ffffff";

  // Add source with the mask geometry directly
  map.addSource(sourceId, {
    type: "geojson",
    data: {
      type: "Feature",
      properties: {},
      geometry: layer.geom!,
    },
  });

  // Use createVignetteRingLayer for individual ring rendering
  map.addLayer(createVignetteRingLayer(layer.id, sourceId, maskColor, layerOpacity));
}

/**
 * Add a prop layer to the map
 * Handles both scattered props (GeometryCollection) and single stamps
 */
function addPropLayer(
  map: MapLibreMap,
  layer: Layer,
  sourceId: string,
  theme: ThemeConfig,
  zoomOffset?: number
): void {
  const propStyleJson = layer.styleJson as {
    propType?: string;
    featureProperties?: Array<{ density_rank: number }>;
    densityRank?: number;
  } | null;
  const propType = propStyleJson?.propType || "tree";
  const propDef = PROP_TYPES.find((p) => p.slug === propType);
  const spriteSlug = propDef?.icon || "prop-tree";
  // Get prop config from theme for size/opacity
  const propConfig = theme.props?.[propType];
  // Check if this is a nature prop for density filtering
  const isNatureProp = NATURE_PROPS.includes(propType as typeof NATURE_PROPS[number]);

  const geom = layer.geom!;
  const featureProps = propStyleJson?.featureProperties;

  if (geom.type === "GeometryCollection") {
    // Scattered props - multiple points with density_rank per feature
    map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: geom.geometries.map((g, i) => ({
          type: "Feature" as const,
          properties: featureProps?.[i] ?? { density_rank: Math.random() },
          geometry: g,
        })),
      },
    });
  } else {
    // Single prop (stamp or individual OSM tree)
    // Use stored densityRank for progressive filtering, fallback 0 = always visible (stamps)
    map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [{
          type: "Feature" as const,
          properties: { density_rank: propStyleJson?.densityRank ?? 0 },
          geometry: geom,
        }],
      },
    });
  }

  const propLayers = createPropSymbolLayer(layer.id, sourceId, spriteSlug, propConfig, {
    isNatureProp,
    minZoom: propConfig?.minZoom,
    shadowDirection: theme.shadowDirection,
    zoomOffset,
  });
  propLayers.forEach((spec) => map.addLayer(spec));
}

/**
 * Add a polygon layer to the map (with optional 3D extrusion)
 */
function addPolygonLayer(
  map: MapLibreMap,
  layer: Layer,
  sourceId: string,
  feature: Feature,
  theme: ThemeConfig
): void {
  // Geometry-aware lookup so a `path_wide` Polygon reads `theme.layers.plaza`
  // (fill tokens) instead of `theme.layers.path_wide` (line tokens).
  const themeStyle = getLayerStyleForGeometry(layer.layerType, layer.geom!.type, theme);
  const style =
    layer.layerType === "custom_polygon"
      ? applyCustomPolygonOverrides(themeStyle, layer.styleJson)
      : themeStyle;
  const styleJson = layer.styleJson as { is3D?: boolean; extrusionHeight?: number } | null;
  const is3D = styleJson?.is3D === true;
  const extrusionHeight = styleJson?.extrusionHeight ?? 10;

  map.addSource(sourceId, { type: "geojson", data: feature });
  if (is3D) {
    // 3D fill-extrusion for buildings/polygons with is3D enabled
    map.addLayer(createThemedFillExtrusion(layer.id, sourceId, style, extrusionHeight));
  } else {
    const specs = createThemedFillLayer(layer.id, sourceId, style);
    specs.forEach((spec) => map.addLayer(spec));
  }
}

/**
 * Add a line layer to the map (visitor receives pre-smoothed geometry from publish)
 */
function addLineLayer(
  map: MapLibreMap,
  layer: Layer,
  sourceId: string,
  feature: Feature,
  theme: ThemeConfig,
): void {
  // Apply per-layer overrides for custom_path, clone to avoid mutating theme
  const style = layer.layerType === "custom_path"
    ? applyCustomPathOverrides(getLayerStyleFromTheme(layer.layerType, theme), layer.styleJson)
    : getLayerStyleFromTheme(layer.layerType, theme);

  map.addSource(sourceId, { type: "geojson", data: feature });
  const lineSpecs = createThemedLineLayer(layer.id, sourceId, style);
  lineSpecs.forEach((spec) => map.addLayer(spec));
}

/**
 * Add a fallback layer for unknown types
 */
function addFallbackLayer(
  map: MapLibreMap,
  layer: Layer,
  sourceId: string,
  feature: Feature,
  theme: ThemeConfig
): void {
  const style = getLayerStyleFromTheme(layer.layerType, theme);
  map.addSource(sourceId, { type: "geojson", data: feature });
  map.addLayer({
    id: layer.id,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": style.fill || "#888",
      "fill-opacity": style.fillOpacity ?? 0.3,
    },
  });
}

/**
 * Options for addLayersToMap
 */
interface AddLayersOptions {
  /** Skip props - handled by Deck.gl overlay for smooth scaling */
  skipPointScenery?: boolean;
  /** Zoom offset for map size presets (shifts prop/layerpoint zoom stops) */
  zoomOffset?: number;
}

/** Add a single layer to the map based on its type */
function addSingleLayer(
  map: MapLibreMap,
  layer: Layer,
  theme: ThemeConfig,
  options?: AddLayersOptions
): void {
  if (!layer.geom) return;

  if (options?.skipPointScenery) {
    if (layer.layerType === "prop") return;
  }

  const sourceId = `layer-${layer.id}`;

  // Skip duplicate sources (can happen after WebGL context restore or stale data)
  if (map.getSource(sourceId)) return;

  if (layer.layerType === "vignette") {
    addVignetteLayer(map, layer, sourceId, theme);
    return;
  }

  const feature: Feature = {
    type: "Feature",
    properties: { name: layer.name },
    geometry: layer.geom,
  };

  const layerStyleJson = layer.styleJson as Record<string, unknown> | null;
  if (layerStyleJson?.isShadow) {
    addShadowLayer(map, layer, sourceId, feature);
    return;
  }

  if (layer.layerType === "prop") {
    addPropLayer(map, layer, sourceId, theme, options?.zoomOffset);
  } else if (
    isDualShapeLayerType(layer.layerType) &&
    (layer.geom.type === "Polygon" || layer.geom.type === "MultiPolygon")
  ) {
    addPolygonLayer(map, layer, sourceId, feature, theme);
  } else if (isPolygonLayerType(layer.layerType)) {
    addPolygonLayer(map, layer, sourceId, feature, theme);
  } else if (isLineLayerType(layer.layerType)) {
    addLineLayer(map, layer, sourceId, feature, theme);
  } else {
    addFallbackLayer(map, layer, sourceId, feature, theme);
  }
}

/**
 * Add all layers to the map with theme-driven styling (sync).
 * For small layer sets (shadows, etc.). For large sets, use addLayersInChunks.
 */
export function addLayersToMap(
  map: MapLibreMap,
  layers: Layer[],
  theme: ThemeConfig,
  options?: AddLayersOptions
): void {
  const sortedLayers = [...layers].sort(
    (a, b) => getLayerSortOrder(a, theme) - getLayerSortOrder(b, theme)
  );
  sortedLayers.forEach((layer) => addSingleLayer(map, layer, theme, options));
}

/**
 * Add layers in chunks to avoid blocking the main thread.
 * Returns after first chunk so map is interactive immediately.
 * Remaining chunks fill in via setTimeout(0) yields.
 * @param isValid — optional check called between chunks; returns false to abort (e.g. on unmount)
 */
export async function addLayersInChunks(
  map: MapLibreMap,
  layers: Layer[],
  theme: ThemeConfig,
  options?: AddLayersOptions,
  chunkSize = 50,
  isValid?: () => boolean
): Promise<void> {
  const sortedLayers = [...layers].sort(
    (a, b) => getLayerSortOrder(a, theme) - getLayerSortOrder(b, theme)
  );

  for (let i = 0; i < sortedLayers.length; i += chunkSize) {
    if (isValid && !isValid()) return;
    const chunk = sortedLayers.slice(i, i + chunkSize);
    chunk.forEach((layer) => addSingleLayer(map, layer, theme, options));
    if (i + chunkSize < sortedLayers.length) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }
}

